import React from "react";
import { MenuItem } from "@mui/material";
import TextField from "@mui/material/TextField";

function ProductCreationForm(props) {
  const {
    mode = "add",
    objectToSendToState,
    handleInputChangeProductCreation,
    groupList,
    subgroupList,
    categoryList,
    subCategoryList,
    unitList,
    itemCodeAlreadyPresentInDb,
    duplicateItemCodeArray,
  } = props;

  const isEditMode = mode === "edit";

  // Shared input class
  const inputClass =
    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer";

  const labelClass =
    "absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto";

  const textFieldSx = {
    "& .MuiInputLabel-root": { fontFamily: "Figtree, sans-serif" },
    "& .MuiInputBase-input:focus": { boxShadow: "none !important" },
  };

  // Helper to build common input props
  const inputProps = (name, type = "text", extra = {}) => ({
    type,
    id: name,
    name,
    className: inputClass,
    placeholder: " ",
    onChange: handleInputChangeProductCreation,
    value: objectToSendToState[name] ?? "",
    ...extra,
  });

  return (
    <div>
      {/* Row 1: Code / OEM / Model No */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div className="relative z-0 m-2" style={{ flexGrow: 1 }}>
          <input
            {...inputProps("code")}
            disabled={isEditMode}
            className={
              inputClass +
              (isEditMode
                ? " cursor-not-allowed opacity-50 border-gray-200"
                : "")
            }
          />
          <label htmlFor="code" className={labelClass}>
            Code
            {isEditMode && (
              <span className="ml-1 text-xs text-gray-400">
                (auto-assigned)
              </span>
            )}
          </label>

          {/* Duplicate code error — edit mode only */}
          {isEditMode && itemCodeAlreadyPresentInDb && (
            <span style={{ color: "#f00" }}>
              Item code already present, please type another one
            </span>
          )}

          {/* Duplicate code list — add mode only, when code field has text */}
          {!isEditMode && objectToSendToState.code && duplicateItemCodeArray?.length > 0 && (
            <div className="mt-1">
              <span style={{ color: "#f00", display: "block", fontSize: "0.875rem" }}>
                Code already exists:
              </span>
              {duplicateItemCodeArray.map((item) => {
                return (
                  <div key={item.code} className="ml-2 text-xs text-gray-500">
                    <span>{item.code}</span>
                  </div>
                );
              })}
            </div>
          )}
          
        </div>

        <div className="relative z-0 m-2" style={{ flexGrow: 1 }}>
          <input {...inputProps("oem")} />
          <label htmlFor="oem" className={labelClass}>
            OEM
          </label>
        </div>

        <div className="relative z-0 m-2" style={{ flexGrow: 1 }}>
          <input {...inputProps("model_no")} />
          <label htmlFor="model_no" className={labelClass}>
            Model No
          </label>
        </div>
      </div>

      {/* Row 2: Description / Specification */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div className="relative z-0 m-2" style={{ flexGrow: 1 }}>
          <input {...inputProps("description")} />
          <label htmlFor="description" className={labelClass}>
            Description
          </label>
        </div>
        <div className="relative z-0 m-2" style={{ flexGrow: 1 }}>
          <input {...inputProps("specification")} />
          <label htmlFor="specification" className={labelClass}>
            Specification
          </label>
        </div>
      </div>

      <div className="p-3">
        <h5>Item Details</h5>
      </div>

      {/* Grid of all remaining fields */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 px-3">
        {/* Group */}
        <div className="relative z-0">
          <TextField
            select
            label="Group"
            name="group"
            fullWidth
            variant="standard"
            value={objectToSendToState.group}
            onChange={handleInputChangeProductCreation}
            sx={textFieldSx}
          >
            <MenuItem value="" disabled>
              Select Group
            </MenuItem>
            {groupList?.map((item) => (
              <MenuItem key={item["GRP CODE"]} value={item["GRP CODE"]?.trim()}>
                {item.DESCRIPTION}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Sub Group */}
        <div className="relative z-0">
          <TextField
            select
            label="Sub Group"
            name="sub_group"
            fullWidth
            variant="standard"
            value={objectToSendToState.sub_group}
            onChange={handleInputChangeProductCreation}
            sx={textFieldSx}
          >
            <MenuItem value="" disabled>
              Select Sub Group
            </MenuItem>
            {subgroupList?.map((item) => (
              <MenuItem key={item["GRP CODE"]} value={item["GRP CODE"]?.trim()}>
                {item.DESCRIPTION}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Category */}
        <div className="relative z-0">
          <TextField
            select
            label="Category"
            name="category"
            fullWidth
            variant="standard"
            value={objectToSendToState.category}
            onChange={handleInputChangeProductCreation}
            sx={textFieldSx}
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            {categoryList?.map((item) => (
              <MenuItem key={item["GRP CODE"]} value={item["GRP CODE"]?.trim()}>
                {item.DESCRIPTION}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Sub Category */}
        <div className="relative z-0">
          <TextField
            select
            label="Sub Category"
            name="sub_category"
            fullWidth
            variant="standard"
            value={objectToSendToState.sub_category}
            onChange={handleInputChangeProductCreation}
            sx={textFieldSx}
          >
            <MenuItem value="" disabled>
              Select Sub Category
            </MenuItem>
            {subCategoryList?.map((item) => (
              <MenuItem key={item["GRP CODE"]} value={item["GRP CODE"]?.trim()}>
                {item.DESCRIPTION}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Volume */}
        <div className="relative z-0">
          <input {...inputProps("volume", "number", { min: "0" })} />
          <label htmlFor="volume" className={labelClass}>
            Volume
          </label>
        </div>

        {/* Weight */}
        <div className="relative z-0">
          <input {...inputProps("weight", "number", { min: "0" })} />
          <label htmlFor="weight" className={labelClass}>
            Weight
          </label>
        </div>

        {/* HSCode */}
        <div className="relative z-0">
          <input {...inputProps("hscode")} />
          <label htmlFor="hscode" className={labelClass}>
            HSCode
          </label>
        </div>

        {/* Origin */}
        <div className="relative z-0">
          <input {...inputProps("origin")} />
          <label htmlFor="origin" className={labelClass}>
            Origin
          </label>
        </div>

        {/* Class */}
        <div className="relative z-0">
          <input {...inputProps("class")} />
          <label htmlFor="class" className={labelClass}>
            Class
          </label>
        </div>

        {/* Unit */}
        <div className="relative z-0">
          <TextField
            select
            label="Unit"
            name="unit"
            fullWidth
            variant="standard"
            value={objectToSendToState.unit}
            onChange={handleInputChangeProductCreation}
            sx={textFieldSx}
          >
            <MenuItem value="" disabled>
              Select Unit
            </MenuItem>
            {unitList?.map((item) => (
              <MenuItem key={item["UNIT"]} value={item["UNIT"]?.trim()}>
                {item.UNIT}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Weblink */}
        <div className="relative z-0">
          <input {...inputProps("weblink")} />
          <label htmlFor="weblink" className={labelClass}>
            Weblink
          </label>
        </div>

        {/* Op Qty */}
        <div className="relative z-0">
          <input {...inputProps("op_qty", "number", { min: "0" })} />
          <label htmlFor="op_qty" className={labelClass}>
            Op Qty
          </label>
        </div>

        {/* Cash Price */}
        <div className="relative z-0">
          <input {...inputProps("cash_price", "number", { min: "0" })} />
          <label htmlFor="cash_price" className={labelClass}>
            Cash Price
          </label>
        </div>

        {/* Credit Price */}
        <div className="relative z-0">
          <input {...inputProps("credit_price", "number", { min: "0" })} />
          <label htmlFor="credit_price" className={labelClass}>
            Credit Price
          </label>
        </div>

        {/* Spl Price */}
        <div className="relative z-0">
          <input {...inputProps("spl_price", "number", { min: "0" })} />
          <label htmlFor="spl_price" className={labelClass}>
            Spl Price
          </label>
        </div>

        {/* Block Price */}
        <div className="relative z-0">
          <input {...inputProps("block_price", "number", { min: "0" })} />
          <label htmlFor="block_price" className={labelClass}>
            Block Price
          </label>
        </div>

        {/* Cost Open */}
        <div className="relative z-0">
          <input {...inputProps("cost_open", "number", { min: "0" })} />
          <label htmlFor="cost_open" className={labelClass}>
            Cost Open
          </label>
        </div>

        {/* DCP */}
        <div className="relative z-0">
          <input {...inputProps("dcp", "number", { min: "0" })} />
          <label htmlFor="dcp" className={labelClass}>
            DCP
          </label>
        </div>

        {/* Min Qty */}
        <div className="relative z-0">
          <input {...inputProps("min_qty", "number", { min: "0" })} />
          <label htmlFor="min_qty" className={labelClass}>
            Min Qty
          </label>
        </div>

        {/* Fixed Cost */}
        <div className="relative z-0">
          <input {...inputProps("fixed_cost", "number", { min: "0" })} />
          <label htmlFor="fixed_cost" className={labelClass}>
            Fixed Cost
          </label>
        </div>

        {/* Message */}
        <div className="relative z-0">
          <input {...inputProps("message")} />
          <label htmlFor="message" className={labelClass}>
            Message
          </label>
        </div>

        {/* Message 2 */}
        <div className="relative z-0">
          <input {...inputProps("message_2")} />
          <label htmlFor="message_2" className={labelClass}>
            Message2
          </label>
        </div>

        {/* Product Type Radio */}
        <fieldset className="mt-4" style={{ display: "flex" }}>
          <div className="flex items-center mb-4">
            <input
              onChange={handleInputChangeProductCreation}
              id="product-type-inventory"
              type="radio"
              name="product_type"
              value="inventory"
              className="w-4 h-4 border-gray-300 focus:ring-2 focus:ring-blue-300"
              checked={objectToSendToState.product_type === "inventory"}
              disabled
            />
            <label
              htmlFor="product-type-inventory"
              className="block ms-2 text-sm font-medium text-gray-400 cursor-not-allowed"
            >
              INVENTORY
            </label>
          </div>
          <div className="flex items-center mb-4 ms-3">
            <input
              onChange={handleInputChangeProductCreation}
              id="product-type-service"
              type="radio"
              name="product_type"
              value="service"
              className="w-4 h-4 border-gray-300 focus:ring-2 focus:ring-blue-300"
              checked={objectToSendToState.product_type === "service"}
            />
            <label
              htmlFor="product-type-service"
              className="block ms-2 text-sm font-medium text-gray-900"
            >
              SERVICE
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  );
}

export default ProductCreationForm;
